import React, { useCallback, useState, useEffect } from 'react';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { mc } from '@shakkerai/report';
import { Tooltip } from '@mantine/core';
import { postShareByUser } from '@shakkerai/services';
import { useElementGenInfo } from '@shakkerai/pro-hooks';
import { ImageShareButton } from '@shakkerai/pro-components';
import { useDownloadState } from '@shakkerai/pro-store';
import { setTempState } from '@starflow/app/store';
import { downloadImage } from '@shakkerai/pro-utils';
import './base.scss';

const sharedImages = {};
const CanvasShareShowKey = '__CanvasShareShowKey__';

export const ImageInnerTools = React.memo(({ element }) => {
  const genInfo = useElementGenInfo(element);

  const [hasDownload, setHasDownload] = useState(false);
  const [showShareTip, setShowShareTip] = useState(false);

  const handleShowGenerationInfo = useCallback(
    (event) => {
      mc('tool.canvas.paramters');
      const offsetRight = event.currentTarget.getBoundingClientRect().right;
      setTempState({ elementInfoPanelId: element.id, elementInfoPanelOffset: { left: offsetRight + 10 } });
    },
    [element],
  );

  const handleDownloadImage = useCallback(() => {
    if (element.type !== 'image') {
      return downloadImage(element.url || element.videoUrl, false);
    }

    useDownloadState.getState().downloadImageWithConfirm(element.url, element.innerImageId);
    setHasDownload(true);

    // 不要重复分享
    if (sharedImages[element.innerImageId]) return;

    sharedImages[element.innerImageId] = true;

    // 设置自动分享
    postShareByUser({
      generateId: element.innerTaskId,
      outputId: element.innerImageId,
      imageId: element.url,
      width: element.width,
      height: element.height,
    });
  }, [element]);

  useEffect(() => {
    const cacheShow = window.localStorage.getItem(CanvasShareShowKey);
    if (hasDownload && !cacheShow) {
      setShowShareTip(true);
    }
  }, [hasDownload]);

  const handleHideTooltip = () => {
    setShowShareTip(false);
    window.localStorage.setItem(CanvasShareShowKey, true);
  };

  const width = Math.round(element?.width * element.scaleX);
  const height = Math.round(element?.height * element.scaleY);
  const isExternalImage = !element?.innerTaskId;
  const isDetailsVary = genInfo?.isDetailsVary;
  const isOverallVary = genInfo?.isOverallVary;

  return (
    <>
      <div className="absolute z-1 right-3 h-7 top-2 px-[12px] flex items-center bg-black/70 rounded-[3px] text-caption-500 text-xs pointer-events-auto">
        <button onClick={handleDownloadImage} className={`flex items-center justify-center h-7 font-normal`}>
          <Icon className="shk-image_Download" color="#fff" fontSize={16} />
        </button>
        {!isExternalImage && (
          <Tooltip
            withArrow
            disabled={!showShareTip}
            position="bottom"
            className="scale-90"
            onMouseOver={handleHideTooltip}
            label={translate('Share your stunning creations！')}
          >
            <ImageShareButton className="ml-[8px]" imageInfo={element} />
          </Tooltip>
        )}
      </div>
      {isExternalImage ? (
        <span
          className={`absolute z-2 bottom-2 right-3 flex items-center whitespace-nowrap h-6 px-2 bg-black/70 rounded-[3px] space-x-1 text-white font-normal text-[11px]`}
        >
          <span className="mr-1.5">
            {width} * {height}
          </span>
          {translate('canvasimg_outside')}
        </span>
      ) : (
        <button
          onClick={handleShowGenerationInfo}
          className={`absolute z-2 bottom-2 right-3 flex items-center whitespace-nowrap h-6 px-2 bg-black/70 rounded-[3px] space-x-1 text-white font-normal text-[11px] pointer-events-auto`}
        >
          <span className="mr-1.5">
            {width} * {height}
          </span>
          <Icon className="icon-clarification" color="#fff" fontSize={12} />
          <span>{translate('generation_info')}</span>
          {(isDetailsVary || isOverallVary) && <div className="w-px h-3 mx-2 bg-white" />}
          {isDetailsVary && <span>{translate('image_tool_vary_details')}</span>}
          {isOverallVary && <span>{translate('image_tool_vary_overall')}</span>}
        </button>
      )}
    </>
  );
});
